import React from "react";
import { Icon } from "./Icon";
import type { IconName } from "@/types/icons";
import { cva, type VariantProps } from "class-variance-authority";

const chipVariants = cva(
  "inline-flex items-center rounded-full px-sm outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 transition-all duration-400",
  {
    variants: {
      size: {
        sm: "py-xs bodytext-md",
        lg: " py-sm bodytext-md",
      },
      color: {
        white:
          "text-white backdrop-blur-sm bg-black/30 hover:bg-black/50 outline-yellow",
        black: "text-black backdrop-blur-sm bg-black/10 outline-purple",
        purple:
          "bg-purple-dark text-white hover:bg-white hover:text-purple-dark",
        "white-gradient":
          "text-white bg-black/30 backdrop-blur-sm relative isolate overflow-hidden before:content-[''] before:absolute before:inset-0 before:-z-10 before:bg-radial-[at_50%_70%] before:from-purple before:via-purple/10 before:to-purple/0 before:opacity-40 hover:before:opacity-70 before:transition-opacity before:duration-200 cursor-pointer outline-yellow",
      },
      active: {
        true: "bg-white hover:bg-white",
      },
    },
    compoundVariants: [
      {
        active: true,
        color: "black",
      },
      {
        active: true,
        color: "white",
        className: "text-black!",
      },
      {
        active: true,
        color: "purple",
        className: "text-purple-dark!",
      },
    ],
    defaultVariants: {
      size: "sm",
      color: "white",
      active: false,
    },
  }
);

interface ChipProps extends VariantProps<typeof chipVariants> {
  children: React.ReactNode;
  icon?: IconName;
  ariaLabel?: string;
  title?: string;
  iconAriaLabel?: string;
  onIconClick?: () => void;
  onClick?: () => void;
  className?: string;
}

export const Chip: React.FC<ChipProps> = ({
  children,
  size = "sm",
  color = "white",
  active = false,
  icon,
  ariaLabel,
  title,
  iconAriaLabel,
  onIconClick,
  onClick,
  className = "",
}) => {
  const iconSize = size === "sm" ? 14 : 18;

  const handleIconClick = (e: React.MouseEvent) => {
    if (onIconClick) {
      e.stopPropagation();
      onIconClick();
    }
  };

  const content = (
    <>
      {children}
      {icon &&
        (onIconClick ? (
          <button
            type="button"
            onClick={handleIconClick}
            className="ml-sm flex items-center rounded-full outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-8 outline-yellow"
            aria-label={iconAriaLabel}
          >
            <Icon name={icon} size={iconSize} decorative />
          </button>
        ) : (
          <span className="ml-sm flex items-center" aria-hidden="true">
            <Icon name={icon} size={iconSize} decorative />
          </span>
        ))}
    </>
  );

  const defaultLabel =
    typeof children === "string" ? children.trim() || undefined : undefined;
  const resolvedAriaLabel = ariaLabel ?? defaultLabel;
  const resolvedTitle = title ?? defaultLabel;

  if (onClick) {
    return (
      <button
        type="button"
        className={chipVariants({
          size,
          color,
          active,
          className: "cursor-pointer " + className,
        })}
        onClick={onClick}
        aria-label={resolvedAriaLabel}
        title={resolvedTitle}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      className={chipVariants({ size, color, active, className })}
      title={resolvedTitle}
    >
      {content}
    </span>
  );
};
